import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useLoaderData } from 'react-router-dom';
import BirthdayService, {
    BirthdayFilterOptions,
    BirthdayGroup,
    BirthdaySearchResponse,
    RangeKey,
} from './BirthdayService';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import BirthdayHeader from './BirthdayHeader';
import BirthdayFilters from './BirthdayFilters';
import BirthdayTabs from './BirthdayTabs';
import BirthdayList from './BirthdayList';
import SessionContext from '../../../library/Core/SessionContext';
import { canExportToExcel, exportToExcel } from '../../../library/Core/SmartFunctions';
import './birthdays.css';

interface LoaderShape {
    data: {
        initialResponse: BirthdaySearchResponse;
        filterOptions: BirthdayFilterOptions;
    };
}

interface BirthdayState {
    searchText: string;
    debouncedSearch: string;
    passOutYear: number | null;
    programId: number | null;
    rangeKey: RangeKey;
    offset: number;
    groups: BirthdayGroup[];
    totalForRange: number;
    tabCounts: Record<RangeKey, number>;
    batchOptions: number[];
    programOptions: { id: number; name: string }[];
    isLoading: boolean;
    isAppending: boolean;
    appendError: boolean;
    initialError: boolean;
    hasMore: boolean;
}

type Action =
    | { type: 'INIT'; payload: { response: BirthdaySearchResponse; filters: BirthdayFilterOptions } }
    | { type: 'SET_SEARCH_TEXT'; value: string }
    | { type: 'APPLY_SEARCH'; value: string }
    | { type: 'SET_BATCH'; value: number | null }
    | { type: 'SET_PROGRAM'; value: number | null }
    | { type: 'SET_RANGE'; value: RangeKey }
    | { type: 'CLEAR_FILTERS' }
    | { type: 'FETCH_REPLACE_START' }
    | { type: 'FETCH_REPLACE_SUCCESS'; response: BirthdaySearchResponse }
    | { type: 'FETCH_REPLACE_FAILURE' }
    | { type: 'FETCH_APPEND_START' }
    | { type: 'FETCH_APPEND_SUCCESS'; response: BirthdaySearchResponse }
    | { type: 'FETCH_APPEND_FAILURE' };

function initialState(loader: LoaderShape): BirthdayState {
    const r = loader.data.initialResponse;
    return {
        searchText: '',
        debouncedSearch: '',
        passOutYear: null,
        programId: null,
        rangeKey: 7,
        offset: r.nextOffset,
        groups: r.groups,
        totalForRange: r.totalForRange,
        tabCounts: r.tabCounts,
        batchOptions: loader.data.filterOptions.passOutYears,
        programOptions: loader.data.filterOptions.programs,
        isLoading: false,
        isAppending: false,
        appendError: false,
        initialError: false,
        hasMore: r.hasMore,
    };
}

function reducer(state: BirthdayState, action: Action): BirthdayState {
    switch (action.type) {
        case 'INIT':
            return {
                ...state,
                groups: action.payload.response.groups,
                totalForRange: action.payload.response.totalForRange,
                tabCounts: action.payload.response.tabCounts,
                offset: action.payload.response.nextOffset,
                hasMore: action.payload.response.hasMore,
                batchOptions: action.payload.filters.passOutYears,
                programOptions: action.payload.filters.programs,
                isLoading: false,
                initialError: false,
            };
        case 'SET_SEARCH_TEXT':
            return { ...state, searchText: action.value };
        case 'APPLY_SEARCH':
            if (action.value === state.debouncedSearch) return state;
            return { ...state, debouncedSearch: action.value };
        case 'SET_BATCH':
            return { ...state, passOutYear: action.value };
        case 'SET_PROGRAM':
            return { ...state, programId: action.value };
        case 'SET_RANGE':
            return { ...state, rangeKey: action.value };
        case 'CLEAR_FILTERS':
            return {
                ...state,
                searchText: '',
                debouncedSearch: '',
                passOutYear: null,
                programId: null,
            };
        case 'FETCH_REPLACE_START':
            return { ...state, isLoading: true, appendError: false };
        case 'FETCH_REPLACE_SUCCESS':
            return {
                ...state,
                groups: action.response.groups,
                totalForRange: action.response.totalForRange,
                tabCounts: action.response.tabCounts,
                offset: action.response.nextOffset,
                hasMore: action.response.hasMore,
                isLoading: false,
                initialError: false,
            };
        case 'FETCH_REPLACE_FAILURE':
            return { ...state, isLoading: false, initialError: true };
        case 'FETCH_APPEND_START':
            return { ...state, isAppending: true, appendError: false };
        case 'FETCH_APPEND_SUCCESS':
            return {
                ...state,
                groups: BirthdayService.mergeAppendGroups(state.groups, action.response.groups),
                tabCounts: action.response.tabCounts,
                totalForRange: action.response.totalForRange,
                offset: action.response.nextOffset,
                hasMore: action.response.hasMore,
                isAppending: false,
            };
        case 'FETCH_APPEND_FAILURE':
            return { ...state, isAppending: false, appendError: true };
        default:
            return state;
    }
}

const UpcomingBirthdays = () => {
    const loader = useLoaderData() as LoaderShape;
    const [state, dispatch] = useReducer(reducer, loader, initialState);
    const { sessionState } = useContext(SessionContext);

    const todayRef = useRef(new Date());
    const showExportButton = canExportToExcel(sessionState);
    const handleExportToExcel = () => exportToExcel(state.groups.flatMap((g) => g.alumni), 'alumni-birthdays');

    useEffect(() => {
        dispatch({ type: 'FETCH_REPLACE_START' });
        BirthdayService.search(
            {
                rangeKey: state.rangeKey,
                debouncedSearch: state.debouncedSearch,
                passOutYear: state.passOutYear,
                programId: state.programId,
                offset: 0,
            },
            false,
        )
            .then((res) => dispatch({ type: 'FETCH_REPLACE_SUCCESS', response: res.data }))
            .catch(() => dispatch({ type: 'FETCH_REPLACE_FAILURE' }));
    }, [state.debouncedSearch, state.passOutYear, state.programId, state.rangeKey]);

    const handleLoadMore = useCallback(() => {
        if (state.isAppending || !state.hasMore) return;
        dispatch({ type: 'FETCH_APPEND_START' });
        BirthdayService.search(
            {
                rangeKey: state.rangeKey,
                debouncedSearch: state.debouncedSearch,
                passOutYear: state.passOutYear,
                programId: state.programId,
                offset: state.offset,
            },
            true,
        )
            .then((res) => dispatch({ type: 'FETCH_APPEND_SUCCESS', response: res.data }))
            .catch(() => dispatch({ type: 'FETCH_APPEND_FAILURE' }));
    }, [
        state.isAppending,
        state.hasMore,
        state.offset,
        state.rangeKey,
        state.debouncedSearch,
        state.passOutYear,
        state.programId,
    ]);

    const hasActiveFilters =
        state.debouncedSearch !== '' || state.passOutYear != null || state.programId != null;

    const handleClearFilters = useCallback(() => {
        dispatch({ type: 'CLEAR_FILTERS' });
    }, []);

    return (
        <>
            <FactLoader show={state.isLoading} />
            <div className="main flex-1">
            <div className="bd-page">
                <div className="container max-1140">
                    <div className="white-block white-block-notopborderradius p-4 p-md-5">
                        <BirthdayHeader
                            today={todayRef.current}
                            totalForRange={state.totalForRange}
                            rangeKey={state.rangeKey}
                        />
                        {showExportButton && (
                            <div className="d-flex justify-content-end mb-3">
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={handleExportToExcel}
                                    disabled={state.groups.length === 0}>
                                    Export to Excel
                                </button>
                            </div>
                        )}
                        <BirthdayFilters
                            searchText={state.searchText}
                            passOutYear={state.passOutYear}
                            programId={state.programId}
                            batchOptions={state.batchOptions}
                            programOptions={state.programOptions}
                            hasActiveFilters={hasActiveFilters}
                            onSearchTextChange={(v) => dispatch({ type: 'SET_SEARCH_TEXT', value: v })}
                            onSearchCommit={(v) => dispatch({ type: 'APPLY_SEARCH', value: v })}
                            onBatchChange={(v) => dispatch({ type: 'SET_BATCH', value: v })}
                            onProgramChange={(v) => dispatch({ type: 'SET_PROGRAM', value: v })}
                            onClearAll={handleClearFilters}
                        />
                        <BirthdayTabs
                            rangeKey={state.rangeKey}
                            counts={state.tabCounts}
                            onChange={(v) => dispatch({ type: 'SET_RANGE', value: v })}
                        />
                        <BirthdayList
                            groups={state.groups}
                            today={todayRef.current}
                            rangeKey={state.rangeKey}
                            totalForRange={state.totalForRange}
                            isLoading={state.isLoading}
                            isAppending={state.isAppending}
                            hasMore={state.hasMore}
                            appendError={state.appendError}
                            initialError={state.initialError}
                            tabCounts={state.tabCounts}
                            hasActiveFilters={hasActiveFilters}
                            onLoadMore={handleLoadMore}
                            onClearFilters={handleClearFilters}
                        />
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default UpcomingBirthdays;
